import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CustomerLayout } from "@/components/Layout/CustomerLayout";
import api from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useAuth } from "@/contexts/AuthContext";
import { PrescriptionUploader } from "@/components/PrescriptionUploader";
import { ChevronLeft, MapPin, Phone, Star, Plus, Minus, CheckCircle2, Loader2 } from "lucide-react";

export default function ProviderDetail() {
  const { key, pid } = useParams();
  const navigate = useNavigate();
  const { lang } = useLang();
  const { formatPrice } = useCurrency();
  const { user } = useAuth();
  const [provider, setProvider] = useState(null);
  const [qtyByItem, setQtyByItem] = useState({});
  const [address, setAddress] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [phone, setPhone] = useState(user?.phone || "");
  const [notes, setNotes] = useState("");
  const [payment, setPayment] = useState("cash");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { api.get(`/providers/${pid}`).then(r => setProvider(r.data)); }, [pid]);

  const bump = (id, delta) => setQtyByItem(q => {
    const cur = q[id] || 0;
    const next = Math.max(0, cur + delta);
    return { ...q, [id]: next };
  });

  const selected = useMemo(() => {
    if (!provider) return [];
    return provider.items
      .filter(it => (qtyByItem[it.id] || 0) > 0)
      .map(it => ({ ...it, quantity: qtyByItem[it.id] }));
  }, [provider, qtyByItem]);

  const subtotal = selected.reduce((s, it) => s + it.price * it.quantity, 0);
  const tax = subtotal * 0.05;
  const total = subtotal + tax;

  const submit = async (e) => {
    e.preventDefault();
    if (selected.length === 0) return setErr(lang === "ar" ? "أضف عنصرًا واحدًا على الأقل" : "Add at least one item");
    setSubmitting(true); setErr("");
    try {
      const { data } = await api.post("/bookings", {
        provider_id: pid,
        items: selected.map(it => ({ item_id: it.id, quantity: it.quantity })),
        address: address || null,
        scheduled_at: scheduledAt || null,
        contact_phone: phone || null,
        payment_method: payment,
        notes: notes || null,
      });
      setSuccess(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || (lang === "ar" ? "فشل الحجز" : "Booking failed"));
    } finally { setSubmitting(false); }
  };

  if (!provider) {
    return <CustomerLayout><div className="p-16 flex justify-center text-[var(--so-text-soft)]"><Loader2 className="w-6 h-6 animate-spin" /></div></CustomerLayout>;
  }

  const terms = provider.terms || { book_en: "Book", book_ar: "احجز", item_en: "Item", item_ar: "عنصر" };
  const bookLabel = lang === "ar" ? terms.book_ar : terms.book_en;
  const accent = provider.accent || "#FF6B00";

  // Modules where scheduled_at matters
  const scheduleModules = ["home_services", "hotels", "flats", "courier", "business"];
  const wantsSchedule = scheduleModules.includes(key);
  const wantsAddress = ["home_services", "courier", "pharmacy", "grocery"].includes(key);

  if (success) {
    return (
      <CustomerLayout>
        <div className="max-w-xl mx-auto px-4 py-16 text-center" data-testid="booking-success">
          <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4" style={{ background: `${accent}1A`, color: accent }}>
            <CheckCircle2 className="w-10 h-10" />
          </div>
          <h1 className="text-3xl font-extrabold">{lang === "ar" ? "تم تأكيد الحجز!" : "Booking confirmed!"}</h1>
          <p className="text-[var(--so-text-soft)] mt-2">
            {lang === "ar" ? "سيتواصل معك المزود قريبًا لتأكيد التفاصيل." : "The provider will reach out shortly to confirm details."}
          </p>
          <div className="so-card p-5 mt-6 text-start">
            <div className="text-sm text-[var(--so-text-soft)]">{lang === "ar" ? "رقم الحجز" : "Booking ID"}</div>
            <div className="font-mono text-xs mt-1 break-all">{success.id}</div>
            <div className="mt-4 flex justify-between font-bold">
              <span>{lang === "ar" ? "الإجمالي" : "Total"}</span>
              <span style={{ color: accent }}>{formatPrice(success.total)}</span>
            </div>
          </div>
          <div className="flex gap-3 mt-8">
            <button onClick={() => navigate("/app/orders")} className="flex-1 so-btn-secondary" data-testid="view-bookings-btn">
              {lang === "ar" ? "عرض حجوزاتي" : "View my bookings"}
            </button>
            <button onClick={() => navigate("/app")} className="flex-1 inline-flex items-center justify-center px-4 py-3 rounded-xl text-white font-semibold" style={{ background: accent }} data-testid="go-home-btn">
              {lang === "ar" ? "العودة للرئيسية" : "Back to home"}
            </button>
          </div>
        </div>
      </CustomerLayout>
    );
  }

  return (
    <CustomerLayout>
      {/* Banner */}
      <div className="relative h-48 md:h-64 overflow-hidden">
        <img src={provider.banner_url || provider.image_url} alt={provider.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 0%, ${accent}CC 100%)` }} />
        <button onClick={() => navigate(`/app/service/${key}`)} className="absolute top-4 start-4 w-10 h-10 rounded-full bg-white/25 backdrop-blur flex items-center justify-center text-white" data-testid="provider-back-btn">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="absolute bottom-4 left-4 right-4 text-white">
          <h1 className="text-2xl md:text-3xl font-extrabold" data-testid="provider-name">{provider.name}</h1>
          <p className="text-white/90 text-sm mt-1 max-w-xl">{provider.description}</p>
          <div className="mt-2 flex items-center gap-4 text-xs">
            <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 fill-amber-300 text-amber-300" /> <b>{provider.rating}</b> ({provider.review_count})</span>
            <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {provider.city}</span>
            {provider.phone && <span className="hidden sm:flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {provider.phone}</span>}
          </div>
        </div>
      </div>

      {/* Pharmacy uses a prescription-photo flow instead of a catalog */}
      {key === "pharmacy" ? (
        <PrescriptionUploader provider={provider} lang={lang} />
      ) : (
      <div className="max-w-5xl mx-auto px-4 py-6 grid md:grid-cols-3 gap-6">
        {/* Items */}
        <div className="md:col-span-2 space-y-3">
          <div className="text-xs font-bold uppercase tracking-wider text-[var(--so-text-soft)]">
            {lang === "ar" ? "اختر العناصر" : "Choose items"}
          </div>
          {provider.items.length === 0 && (
            <div className="so-card p-8 text-center text-[var(--so-text-soft)]">
              {lang === "ar" ? "لا توجد عناصر بعد" : "No items yet"}
            </div>
          )}
          {provider.items.map(it => {
            const qty = qtyByItem[it.id] || 0;
            return (
              <div key={it.id} className="so-card p-4 flex items-center gap-3" data-testid={`item-${it.id}`}>
                {it.image_url && <img src={it.image_url} alt={it.name} className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{it.name}</div>
                  <div className="text-xs text-[var(--so-text-soft)] line-clamp-2">{it.description}</div>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="font-bold" style={{ color: accent }}>{formatPrice(it.price)}</span>
                    {it.unit && <span className="text-xs text-[var(--so-text-soft)]">/ {it.unit}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {qty > 0 && (
                    <>
                      <button onClick={() => bump(it.id, -1)} className="w-8 h-8 rounded-full border border-[var(--so-border)] flex items-center justify-center" data-testid={`item-minus-${it.id}`}>
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="font-bold w-6 text-center" data-testid={`item-qty-${it.id}`}>{qty}</span>
                    </>
                  )}
                  <button onClick={() => bump(it.id, 1)} className="w-8 h-8 rounded-full flex items-center justify-center text-white" style={{ background: accent }} data-testid={`item-plus-${it.id}`}>
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Booking form */}
        <form onSubmit={submit} className="so-card p-5 h-fit sticky top-20 space-y-3" data-testid="booking-form">
          <div className="font-bold text-lg">{lang === "ar" ? "تفاصيل الحجز" : "Booking details"}</div>

          {wantsAddress && (
            <input className="so-input" placeholder={lang === "ar" ? "العنوان" : "Delivery / service address"} value={address} onChange={e => setAddress(e.target.value)} data-testid="booking-address" />
          )}
          {wantsSchedule && (
            <input type="datetime-local" className="so-input" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} data-testid="booking-scheduled-at" />
          )}
          <input className="so-input" placeholder={lang === "ar" ? "رقم الاتصال" : "Contact phone"} value={phone} onChange={e => setPhone(e.target.value)} data-testid="booking-phone" />
          <textarea className="so-input min-h-[70px]" placeholder={lang === "ar" ? "ملاحظات (اختياري)" : "Notes (optional)"} value={notes} onChange={e => setNotes(e.target.value)} data-testid="booking-notes" />

          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[var(--so-text-soft)] mb-2">
              {lang === "ar" ? "طريقة الدفع" : "Payment"}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {["cash", "wallet"].map(m => (
                <button
                  type="button"
                  key={m}
                  onClick={() => setPayment(m)}
                  data-testid={`payment-${m}`}
                  className={`py-2 rounded-lg border text-sm font-semibold ${payment === m ? "text-white" : "text-[var(--so-text-soft)]"}`}
                  style={payment === m ? { background: accent, borderColor: accent } : { borderColor: "var(--so-border)" }}
                >
                  {m === "cash" ? (lang === "ar" ? "نقدًا" : "Cash") : (lang === "ar" ? "المحفظة" : "Wallet")}
                </button>
              ))}
              <button type="button" disabled data-testid="payment-mbok" className="py-2 rounded-lg border border-dashed text-xs font-semibold text-[var(--so-text-soft)] opacity-70 cursor-not-allowed flex items-center justify-center gap-1">
                mBOK 🇸🇩 <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 uppercase">{lang === "ar" ? "قريباً" : "Soon"}</span>
              </button>
              <button type="button" disabled data-testid="payment-mpay" className="py-2 rounded-lg border border-dashed text-xs font-semibold text-[var(--so-text-soft)] opacity-70 cursor-not-allowed flex items-center justify-center gap-1">
                mPay 🇸🇩 <span className="text-[9px] px-1 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 uppercase">{lang === "ar" ? "قريباً" : "Soon"}</span>
              </button>
            </div>
          </div>

          <div className="pt-3 border-t border-[var(--so-border)] space-y-1 text-sm">
            <div className="flex justify-between text-[var(--so-text-soft)]">
              <span>{lang === "ar" ? "المجموع الفرعي" : "Subtotal"}</span>
              <span data-testid="booking-subtotal">{formatPrice(subtotal)}</span>
            </div>
            <div className="flex justify-between text-[var(--so-text-soft)]">
              <span>{lang === "ar" ? "ضريبة" : "Tax (5%)"}</span>
              <span>{formatPrice(tax)}</span>
            </div>
            <div className="flex justify-between font-bold text-base pt-1">
              <span>{lang === "ar" ? "الإجمالي" : "Total"}</span>
              <span style={{ color: accent }} data-testid="booking-total">{formatPrice(total)}</span>
            </div>
          </div>

          {err && <div className="text-sm text-red-500" data-testid="booking-error">{err}</div>}
          <button type="submit" disabled={submitting || selected.length === 0} className="w-full inline-flex items-center justify-center gap-2 font-semibold px-4 py-3 rounded-xl text-white disabled:opacity-50" style={{ background: accent }} data-testid="booking-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : bookLabel}
          </button>
        </form>
      </div>
      )}
    </CustomerLayout>
  );
}
